import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shallowEqual } from './utils';

type SearchParamProps = Record<string, string | number | undefined>;

export const useRouting = (filterKeys: string[]) => {
  const [aborterRef, setAbortRef] = useState(new AbortController());
  const [searchParams, setSearchParamsFn] = useSearchParams();
  const [filters, setFilters] = useState<SearchParamProps>({});

  const getFiltersParams = (queryParams: string, keys: string[]) => {
    const newSearchParams = new URLSearchParams(queryParams);
    const newQueryParams: { [key: string]: string | number } = {};

    newSearchParams.forEach((value, key) => {
      if (keys.includes(key)) {
        newQueryParams[key] = Number.isInteger(Number(value)) &&
          (value.charAt(0) !== '0' || value.length === 1)
        ? Number(value)
        : value;
      }
    });
    return newQueryParams;
  };

  useEffect(() => {
    const newFilters = getFiltersParams(location.search, filterKeys);

    if (!shallowEqual(filters, newFilters)) {
      setFilters(newFilters);
    }
  }, [location.search, searchParams]);

  const handleFirstLoad = useCallback(() => {
    if (aborterRef) aborterRef.abort();
    setAbortRef(new AbortController());
  }, [aborterRef]);

  const getSearchParam = useCallback(
    (param: string) => {
      return searchParams.get(param) || undefined;
    },
    [searchParams]
  );

  const setSearchParams = useCallback(
    (params: SearchParamProps) => {
      setSearchParamsFn((prevSearchParams) => {
        const newSearchParams = new URLSearchParams(prevSearchParams);
        Object.keys(params).forEach((key) => {
          newSearchParams.set(key, params[key] as string);
        });
        return newSearchParams;
      });
    },
    [setSearchParamsFn]
  );

  const clearAndSetSearchParams = useCallback(
    (params: SearchParamProps, paramsToKeep: string[] = []) => {
      setSearchParamsFn((prevParams) => {
        const newSearchParams = new URLSearchParams();

        // First, add the parameters we want to keep
        paramsToKeep.forEach((key) => {
          const value = prevParams.get(key);
          if (value !== null) {
            newSearchParams.set(key, value);
          }
        });

        // Then, add or overwrite with the new parameters
        Object.keys(params).forEach((key) => {
          newSearchParams.set(key, params[key] as string);
        });

        return newSearchParams;
      });
    },
    [setSearchParamsFn]
  );

  const cleanSearchParams = useCallback(
    (params?: string[]) => {
      const newSearchParams = new URLSearchParams(location.search);
      const currentKeys: string[] = [];
      newSearchParams.forEach((_, k) => currentKeys.push(k));
      (params || currentKeys).forEach((key) => {
        newSearchParams.delete(key);
      });
      setSearchParamsFn(newSearchParams);
    },
    [setSearchParamsFn]
  );

  // FOOD-47 — set some keys and delete others in ONE history update. `searchParams` (and so
  // setSearchParams' functional updater) is a useMemo keyed on location.search from THIS
  // render — it does not see a preceding setSearchParams call in the same synchronous handler,
  // so two such calls back to back clobber each other, the second computed from the same stale
  // snapshot as the first. Needed for mutually-exclusive filter pairs, where picking one has to
  // clear the other in the same update: doing that as a set call plus a separate clear call
  // would lose the set.
  const setAndClearSearchParams = useCallback(
    (params: SearchParamProps, keysToClear: string[] = []) => {
      setSearchParamsFn((prevSearchParams) => {
        const newSearchParams = new URLSearchParams(prevSearchParams);
        keysToClear.forEach((key) => newSearchParams.delete(key));
        Object.keys(params).forEach((key) => {
          newSearchParams.set(key, params[key] as string);
        });
        return newSearchParams;
      });
    },
    [setSearchParamsFn]
  );

  const searchParamsUpdated = useMemo(() => ({ updated: true }), [searchParams]);

  return {
    aborterRef,
    cleanSearchParams,
    getSearchParam,
    handleFirstLoad,
    setSearchParams,
    setAndClearSearchParams,
    searchParamsUpdated,
    clearAndSetSearchParams,
    filters,
  };
};
